import React from "react";
import { ClipboardCheck, Lock } from "lucide-react";

type SigSummary = { signed?: boolean; signedAt?: string; signerName?: string };

export type POHSummary = {
  pickup?: {
    consignerSignature?: SigSummary;
    driverSignature?: SigSummary;
    status?: string;
    lockedAt?: string;
  };
  delivery?: {
    customerSignature?: SigSummary;
    driverSignature?: SigSummary;
    status?: string;
    lockedAt?: string;
  };
} | null | undefined;

const fmt = (d?: string) =>
  d ? new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null;

const sigOf = (s: any) => ({
  signed: !!(s?.signedAt || s?.signerName || s?.signatureData),
  name: s?.signerName,
  at: s?.signedAt,
});

const POHStatusDots: React.FC<{
  poh: POHSummary;
  pohLevel?: 0 | 1 | 2;
  size?: "sm" | "md";
  showLabel?: boolean;
}> = ({ poh, pohLevel = 1, size = "sm", showLabel = false }) => {
  // Level 2 is shipper + consignee only — two signatures, no driver slots.
  const hideDriver = pohLevel === 2;
  const consigner = sigOf((poh?.pickup as any)?.consignerSignature);
  const pickupDriver = sigOf((poh?.pickup as any)?.driverSignature);
  const consignee = sigOf((poh?.delivery as any)?.customerSignature);
  const deliveryDriver = sigOf((poh?.delivery as any)?.driverSignature);

  const parts = hideDriver
    ? [consigner, consignee]
    : [consigner, pickupDriver, consignee, deliveryDriver];
  const count = parts.filter((p) => p.signed).length;
  const total = parts.length;

  const fullyLocked = !!(poh as any)?.pickup?.lockedAt && !!(poh as any)?.delivery?.lockedAt;

  let chipClass = "bg-gray-100 text-gray-600 border-gray-200";
  if (count === total) chipClass = "bg-green-50 text-green-700 border-green-200";
  else if (count > 0) chipClass = "bg-blue-50 text-blue-700 border-blue-200";

  let segActive = "bg-gray-300";
  if (count === total) segActive = "bg-green-500";
  else if (count > 0) segActive = "bg-blue-500";

  const tip = [
    `Proof of Handover — ${count}/${total} signed`,
    "",
    `Pickup:`,
    `  Consignor: ${consigner.signed ? `${consigner.name ?? "signed"} (${fmt(consigner.at) ?? "—"})` : "pending"}`,
    ...(hideDriver
      ? []
      : [`  Driver:    ${pickupDriver.signed ? `${pickupDriver.name ?? "signed"} (${fmt(pickupDriver.at) ?? "—"})` : "pending"}`]),
    `Delivery:`,
    ...(hideDriver
      ? []
      : [`  Driver:    ${deliveryDriver.signed ? `${deliveryDriver.name ?? "signed"} (${fmt(deliveryDriver.at) ?? "—"})` : "pending"}`]),
    `  Consignee: ${consignee.signed ? `${consignee.name ?? "signed"} (${fmt(consignee.at) ?? "—"})` : "pending"}`,
  ].join("\n");

  const iconSize = size === "md" ? "h-3.5 w-3.5" : "h-3 w-3";

  return (
    <span
      title={tip}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide cursor-help ${chipClass}`}
    >
      {fullyLocked ? <Lock className={iconSize} /> : <ClipboardCheck className={iconSize} />}
      <span>POH</span>
      <span className="inline-flex items-center gap-[2px]">
        {parts.map((p, i) => (
          <span
            key={i}
            className={`h-1.5 w-2 rounded-[1px] ${p.signed ? segActive : "bg-gray-200"}`}
          />
        ))}
      </span>
      <span className="font-mono">
        {count}/{total}
      </span>
      {showLabel && fullyLocked && <span className="text-[9px] font-normal opacity-70">locked</span>}
    </span>
  );
};

export default POHStatusDots;
